<template>
  <div class="flex items-center">
    <div class="on-left">
      <q-icon v-if="icon" :name="icon" />
      <CountrySelect
        :model-value="country"
        @update:model-value="onCountryChanged($event)"
      />
    </div>
    <q-input
      :label="label"
      :model-value="nationalNumber"
      :rules="rules"
      mask="(###) ###-####"
      unmasked-value
      hide-bottom-space
      v-bind="$attrs"
      @update:model-value="onNationalNumberChanged"
      type="tel"
      style="flex-grow: 1"
    />
  </div>
</template>

<script lang="ts">
// Adapted from https://github.com/iamstevendao/vue-tel-input
// Copyright (c) 2018 Steven Dao - MIT License

import { defineComponent } from 'vue';

import CountrySelect from './CountrySelect.vue';
import { Country, getCountry } from './countries';
import {
  extractNationalNumber,
  extractCountryCode,
  toInternationalFormat,
  validatePhoneNumber,
  parseInternationalNumber,
  isPhoneValid,
} from './utils';

export default defineComponent({
  emits: ['update:modelValue'],
  components: { CountrySelect },
  props: {
    modelValue: {
      type: String,
      default: null,
    },
    defaultCountry: {
      type: String,
      default: 'US',
    },
    icon: {
      type: String,
      default: undefined,
    },
    label: {
      type: String,
      default: undefined,
    },
    required: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    rules(): ((val: string) => boolean|string)[] {
      return [this.isValidNumber];
    },
    countryCode(): string {
      return extractCountryCode(this.modelValue, this.defaultCountry);
    },
    dialCode(): string {
      const country = getCountry(this.countryCode);
      return country ? `+${country.dialCode}` : '';
    },
    country(): Country {
      return getCountry(this.countryCode)!;
    },
    nationalNumber(): string {
      return extractNationalNumber(this.modelValue, this.country.dialCode);
    },
  },
  methods: {
    isValidNumber(val: string): boolean|string {
      if (!val || val.trim() === '') {
        return this.required ? 'This value is required' : true;
      }
      if (!isPhoneValid(toInternationalFormat(val, this.countryCode), this.required)) {
        return 'Phone number must be exactly 10 digits';
      }
      return true;
    },
    onCountryChanged(country: Country) {
      const nationalNumber = this.nationalNumber || '';
      const newNumber = toInternationalFormat(nationalNumber, country.iso2);
      this.$emit('update:modelValue', newNumber);
    },
    onNationalNumberChanged(phoneNumber: string|number|null) {
      const nationalNumber = phoneNumber ? String(phoneNumber) : '';
      if (!nationalNumber) {
        this.$emit('update:modelValue', this.dialCode);
        return;
      }

      // Check if it's an international number and update country select accordingly
      const parsedIntl = parseInternationalNumber(nationalNumber);
      if (parsedIntl) {
        this.$emit('update:modelValue', parsedIntl.number);
        return;
      }

      // Parse with country context to properly handle national format (e.g., leading 0)
      const countryCode = this.countryCode || this.defaultCountry;
      const newNumber = toInternationalFormat(nationalNumber, countryCode);
      this.$emit('update:modelValue', newNumber);
    },
  },
});
</script>
